import { useAuth0 } from "@auth0/auth0-react";
import { FormattedMessage } from "react-intl";

import { useLayoutStore } from "app/stores/useLayoutStore";
import { Button } from "ui/Button/Button";
import { Modal } from "ui/Modal/Modal";
import { BodyM, DetailM, HeaderS } from "ui/Typography/Typography";

// Keeps the first 3 characters (enough to tell "de_…"/"nl_…" hub accounts and "hub.…" shared
// accounts from personal ones) and masks the rest with one dot per character, so the length
// still matches. At least one character is always masked.
const VISIBLE_EMAIL_CHARACTERS = 3;

export const maskEmail = (email: string): string => {
  const [local, domain] = email.split("@");
  if (!domain) return email;
  const visible = Math.min(VISIBLE_EMAIL_CHARACTERS, Math.max(local.length - 1, 0));
  return `${local.slice(0, visible)}${"•".repeat(local.length - visible)}@${domain}`;
};

// Shown when hub-one-core rejects the account for missing the `hubone:all` permission,
// e.g. a personal account instead of the hub account. Only a new login can fix it.
export function MissingHubOnePermissionModal() {
  const isOpen = useLayoutStore((state) => state.showMissingHubOnePermissionModal);
  const { logout, user } = useAuth0();

  const onClickLogout = () => {
    useLayoutStore.setState({ showMissingHubOnePermissionModal: false });
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  return (
    <Modal
      trapFocus={false}
      closeOnOverlayClick={false}
      isCentered
      isOpen={isOpen}
      onClose={() => {}}
    >
      <Modal.Content marginX="s300">
        <Modal.Body display="flex" flexDir="column" alignItems="center" textAlign="center" p="s200">
          <HeaderS mt="s200">
            <FormattedMessage id="core.components.missing-hubone-permission-modal.title" />
          </HeaderS>
          <BodyM mt="s100" color="grey.700">
            <FormattedMessage id="core.components.missing-hubone-permission-modal.description" />
          </BodyM>
          {user?.email && (
            <DetailM mt="s150" color="grey.700" data-testid="missing-hubone-permission-account">
              <FormattedMessage
                id="core.components.missing-hubone-permission-modal.logged-in-as"
                values={{ email: maskEmail(user.email) }}
              />
            </DetailM>
          )}
          <DetailM mt="s200" color="grey.500">
            <FormattedMessage id="core.components.missing-hubone-permission-modal.help" />
          </DetailM>
        </Modal.Body>
        <Modal.Footer paddingX="s200" pt="s100" flexDir="column">
          <Button onClick={onClickLogout} variant="primary" size="lg" isFullWidth>
            <FormattedMessage id="core.components.missing-hubone-permission-modal.log-out" />
          </Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
}
